/**
 * HitlResponseRenderer — friendly display for the HITL Decisions card body.
 *
 * Replaces the old "schema ? JsonSchemaForm-readonly : raw-JSON-pre" dispatch
 * in ``WorkflowHitlHistorySection``. A single smart renderer that:
 *
 *   - filters internal audit metadata (answered_by_oid, answered_by_name, …)
 *   - hoists decision-like keys to the top
 *   - renders decision tokens as ``<icon> <text>`` (✓ Approved, ✗ Rejected, …)
 *   - turns array-of-objects into labeled sub-cards ("Mortgagee 1", "Mortgagee 2")
 *   - preserves multi-line strings, formats ISO-8601 dates, links URLs
 *   - falls back to a defensive ``<details>`` JSON dump past depth 3
 *
 * Workflows can override field labels by setting ``properties[key].title`` in
 * the question schema — the renderer reads it. ``description`` becomes a
 * hover tooltip on the label.
 *
 * Shared between ora's drawer and the Outlook plugin's drawer (both consume
 * ``WorkflowHitlHistorySection`` from this directory).
 */

import CustomTooltip from "~/common/components/CustomTooltip";
import { formatCapturedAt } from "./WorkflowArtifactsSection";
import { classifyHitlResponse } from "../hitlSummary";
import type { HitlBadgeKind } from "../hitlSummary";
import {
  classifySensitivity,
  debugViewsBypassRedaction,
  maskValue,
  redactTree,
} from "../sensitivity";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface HitlResponseRendererProps {
  /** The persisted HITL response payload. */
  response: unknown;
  /** Optional JSON Schema (the same schema the question carried) so labels
   *  and field order can use ``properties[key].title``. */
  schema?: Record<string, unknown> | null;
  /** Keys hidden from rendering. Defaults filter audit-trail metadata. */
  excludeKeys?: string[];
  /** When set, the corresponding event_type ("timed_out" / "cancelled") drives
   *  the decision-badge fallback when the response is null. ``"asked"`` with
   *  a null response is a pending HITL ask (question recorded, no answer
   *  yet) — rendered as "Awaiting decision" instead of the bare "—"
   *  placeholder. */
  eventType?: string;
}

const DEFAULT_EXCLUDE_KEYS = [
  "answered_by_oid",
  "answered_by_name",
  "_correlation_id",
];

const DECISION_KEYS = new Set([
  "decision",
  "choice",
  "response",
  "action",
  "result",
]);

const MAX_DEPTH = 3;

const PLACEHOLDER = "—";

// ---------------------------------------------------------------------------
// Decision badge — icon + text, never one or the other
// ---------------------------------------------------------------------------

const BADGE_GLYPH: Record<HitlBadgeKind, string> = {
  approve: "✓",
  reject: "✗",
  corrections: "✏",
  timeout: "⏱",
  unknown: "•",
};

const BADGE_LABEL: Record<HitlBadgeKind, string> = {
  approve: "Approved",
  reject: "Rejected",
  corrections: "Approved with corrections",
  timeout: "Timed out",
  unknown: "",
};

const BADGE_TONE: Record<HitlBadgeKind, string> = {
  approve: "text-primary-success-700",
  reject: "text-primary-error-700",
  corrections: "text-primary-warning-700",
  timeout: "text-primary-gray-light-600",
  unknown: "text-primary-gray-light-700",
};

function renderDecisionBadge(
  rawValue: unknown,
  eventType: string | undefined,
): JSX.Element {
  // Build a synthetic single-key dict so classifyHitlResponse can match it
  // regardless of the originating key (decision/choice/response/action/result).
  const synthetic =
    typeof rawValue === "string" ? { decision: rawValue } : null;
  const kind = classifyHitlResponse(synthetic, eventType);
  const glyph = BADGE_GLYPH[kind];
  const label =
    BADGE_LABEL[kind] ||
    (typeof rawValue === "string" ? titleCase(rawValue) : String(rawValue));
  return (
    <span className={`inline-flex items-center gap-1.5 ${BADGE_TONE[kind]}`}>
      <span aria-hidden="true" className="text-base font-semibold leading-none">
        {glyph}
      </span>
      <span>{label}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Label + ordering helpers
// ---------------------------------------------------------------------------

function titleCase(snake: string): string {
  return snake
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

const SINGULAR_OVERRIDES: Record<string, string> = {
  mortgagees: "Mortgagee",
  policies: "Policy",
  addresses: "Address",
  beneficiaries: "Beneficiary",
};

function singularize(plural: string): string {
  const lower = plural.toLowerCase();
  if (SINGULAR_OVERRIDES[lower]) return SINGULAR_OVERRIDES[lower];
  // Fallback: drop trailing "s" if the word doesn't already end in "ss".
  if (lower.length > 1 && lower.endsWith("s") && !lower.endsWith("ss")) {
    const stem = plural.slice(0, -1);
    return titleCase(stem);
  }
  return titleCase(plural);
}

function getSchemaProperty(
  schema: Record<string, unknown> | null | undefined,
  key: string,
): Record<string, unknown> | undefined {
  if (!schema) return undefined;
  const props = schema.properties as Record<string, unknown> | undefined;
  if (!props || typeof props !== "object") return undefined;
  const def = props[key];
  return def && typeof def === "object" && !Array.isArray(def)
    ? (def as Record<string, unknown>)
    : undefined;
}

function labelFor(
  key: string,
  schemaProperty?: Record<string, unknown>,
): { label: string; description?: string } {
  const titleVal =
    schemaProperty && typeof schemaProperty.title === "string"
      ? (schemaProperty.title as string)
      : null;
  const descVal =
    schemaProperty && typeof schemaProperty.description === "string"
      ? (schemaProperty.description as string)
      : undefined;
  return { label: titleVal || titleCase(key), description: descVal };
}

function orderFields(
  keys: string[],
  schema: Record<string, unknown> | null | undefined,
): string[] {
  const decisionKeys: string[] = [];
  const remaining: string[] = [];
  for (const k of keys) {
    if (DECISION_KEYS.has(k)) decisionKeys.push(k);
    else remaining.push(k);
  }
  if (schema) {
    const props = schema.properties as Record<string, unknown> | undefined;
    if (props && typeof props === "object") {
      const schemaOrder = Object.keys(props);
      remaining.sort((a, b) => {
        const ai = schemaOrder.indexOf(a);
        const bi = schemaOrder.indexOf(b);
        // Schema-known keys retain schema order; unknown keys keep relative order at the end.
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });
    }
  }
  return [...decisionKeys, ...remaining];
}

// ---------------------------------------------------------------------------
// Primitive formatting
// ---------------------------------------------------------------------------

const ISO_DATE_RE =
  /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

const URL_RE = /^https?:\/\/[^\s]+$/i;

function isPlaceholder(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim().length === 0) return true;
  return false;
}

function formatPrimitive(value: unknown): string {
  if (isPlaceholder(value)) return PLACEHOLDER;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string") {
    if (ISO_DATE_RE.test(value)) {
      const formatted = formatCapturedAt(value);
      if (formatted) return formatted;
    }
    return value;
  }
  return String(value);
}

// ---------------------------------------------------------------------------
// Value rendering
// ---------------------------------------------------------------------------

function isObjectLike(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function isArrayOfObjects(v: unknown): v is Record<string, unknown>[] {
  return (
    Array.isArray(v) &&
    v.length > 0 &&
    v.every((it) => isObjectLike(it))
  );
}

function isMultilineString(v: unknown): v is string {
  return typeof v === "string" && v.includes("\n");
}

function isUrlString(v: unknown): v is string {
  return typeof v === "string" && URL_RE.test(v.trim());
}

function renderValue(
  value: unknown,
  schemaProperty: Record<string, unknown> | undefined,
  depth: number,
  keyHint?: string,
): JSX.Element {
  // Mask sensitive fields — never show cleartext in HITL history.
  // "password" kind → length-matched stars; "secret"/"pii" → flat [REDACTED].
  const sensitiveKind = classifySensitivity(schemaProperty, keyHint);
  if (sensitiveKind !== null && !isPlaceholder(value)) {
    const masked = maskValue(value, sensitiveKind);
    return <span className="text-sm text-primary-gray-light-500">{masked}</span>;
  }

  if (depth >= MAX_DEPTH) {
    // Redact sensitive subtree values before stringifying in production.
    const safeValue = debugViewsBypassRedaction()
      ? value
      : redactTree(value, schemaProperty);
    return (
      <details className="text-xs text-primary-gray-light-600">
        <summary className="cursor-pointer">View raw JSON</summary>
        <pre className="mt-1 bg-primary-gray-light-50 rounded p-2 overflow-auto whitespace-pre-wrap font-mono">
          {JSON.stringify(safeValue, null, 2)}
        </pre>
      </details>
    );
  }

  if (isPlaceholder(value) || typeof value === "boolean") {
    return (
      <span className="text-sm text-primary-gray-light-900">
        {formatPrimitive(value)}
      </span>
    );
  }

  if (isUrlString(value)) {
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-sunrise underline hover:no-underline break-all"
      >
        {value}
      </a>
    );
  }

  if (isMultilineString(value)) {
    return (
      <div className="text-sm text-primary-gray-light-900 whitespace-pre-wrap">
        {value}
      </div>
    );
  }

  if (typeof value === "string" || typeof value === "number") {
    return (
      <span className="text-sm text-primary-gray-light-900">
        {formatPrimitive(value)}
      </span>
    );
  }

  if (isArrayOfObjects(value)) {
    const itemsSchema =
      schemaProperty && isObjectLike(schemaProperty.items)
        ? (schemaProperty.items as Record<string, unknown>)
        : undefined;
    const itemTitle =
      itemsSchema && typeof itemsSchema.title === "string"
        ? (itemsSchema.title as string)
        : keyHint
          ? singularize(keyHint)
          : "Item";
    return (
      <div className="flex flex-col gap-2">
        {value.map((item, idx) => (
          <section
            key={idx}
            className="rounded-md border border-primary-gray-light-200 bg-primary-gray-light-25 px-3 py-2"
          >
            <h5 className="text-xs font-semibold text-primary-gray-light-600 mb-1">
              {itemTitle} {idx + 1}
            </h5>
            <ResponseObject
              data={item}
              schema={itemsSchema}
              depth={depth + 1}
            />
          </section>
        ))}
      </div>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <span className="text-sm text-primary-gray-light-500">{PLACEHOLDER}</span>
      );
    }
    if (value.length <= 3) {
      return (
        <span className="text-sm text-primary-gray-light-900">
          {value.map((v) => formatPrimitive(v)).join(", ")}
        </span>
      );
    }
    return (
      <ul className="list-disc pl-5 text-sm text-primary-gray-light-900">
        {value.map((v, i) => (
          <li key={i}>{formatPrimitive(v)}</li>
        ))}
      </ul>
    );
  }

  if (isObjectLike(value)) {
    return (
      <ResponseObject data={value} schema={schemaProperty} depth={depth + 1} />
    );
  }

  return (
    <span className="text-sm text-primary-gray-light-900">
      {String(value)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Definition list — the workhorse for object renders
// ---------------------------------------------------------------------------

function ResponseObject({
  data,
  schema,
  depth,
  excludeKeys,
}: {
  data: Record<string, unknown>;
  schema?: Record<string, unknown>;
  depth: number;
  excludeKeys?: string[];
}) {
  const filtered = Object.entries(data).filter(
    ([k]) => !(excludeKeys ?? []).includes(k),
  );
  if (filtered.length === 0) {
    return (
      <span className="text-sm text-primary-gray-light-500">{PLACEHOLDER}</span>
    );
  }

  const orderedKeys = orderFields(
    filtered.map(([k]) => k),
    schema,
  );
  const map = Object.fromEntries(filtered);

  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5">
      {orderedKeys.map((key) => {
        const value = map[key];
        const schemaProp = getSchemaProperty(schema, key);
        const { label, description } = labelFor(key, schemaProp);
        const isDecision = DECISION_KEYS.has(key);
        const term = (
          <dt className="text-xs font-semibold text-primary-gray-light-600 self-start pt-0.5">
            {label}
          </dt>
        );
        return (
          <div key={key} className="contents">
            {description ? (
              // The field's schema description in ora's tooltip, not a native
              // title (story 28633). Node title + describeChild: the term
              // keeps its label as its text and name.
              <CustomTooltip
                id={`hitl-response-${key}`}
                title={<span>{description}</span>}
                describeChild
                placement="top-start"
                arrow
                isOnModal
              >
                {term}
              </CustomTooltip>
            ) : (
              term
            )}
            <dd>
              {isDecision
                ? renderDecisionBadge(value, undefined)
                : renderValue(value, schemaProp, depth, key)}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function HitlResponseRenderer({
  response,
  schema,
  excludeKeys,
  eventType,
}: HitlResponseRendererProps): JSX.Element {
  const exclude = excludeKeys ?? DEFAULT_EXCLUDE_KEYS;

  if (response === null || response === undefined) {
    if (eventType === "timed_out" || eventType === "cancelled") {
      return renderDecisionBadge(eventType, eventType);
    }
    if (eventType === "asked") {
      return (
        <span className="text-sm text-primary-gray-light-500">
          Awaiting decision
        </span>
      );
    }
    return (
      <span className="text-sm text-primary-gray-light-500">{PLACEHOLDER}</span>
    );
  }

  // Non-object root: render the value directly without a definition list.
  if (!isObjectLike(response)) {
    if (typeof response === "string") {
      // Treat a bare-string response as a decision token.
      return renderDecisionBadge(response, eventType);
    }
    return renderValue(response, undefined, 0);
  }

  return (
    <ResponseObject
      data={response}
      schema={schema ?? undefined}
      depth={0}
      excludeKeys={exclude}
    />
  );
}

export default HitlResponseRenderer;
